// ============================================================================
// AccountStartPanel — the Account start CARD, on the New Accounts card detail
// and on a building's Info tab.
//
// It shows two dates and offers three acts:
//   · it started            → confirm_account_started (marks the building live
//                             and tells Finance to start billing and paying)
//   · the start date moved  → update_account_start_date, planned side, GATED:
//                             a building that has not opened owes a reason
//   · correct the real start date → update_account_start_date, actual side,
//                             not gated, because that is a record being tidied
//
// ⛔ REWRITTEN 2026-09-21. Two things were wrong with the old panel.
//
// First, "Edit planned date" was a bare date box. It sent no reason, so on any
// building that has not opened the database refused it every single time —
// Ben and Spencer both hit that refusal on Georgetown Surgery Center and
// neither could comply, because there was nothing to type the answer into. The
// reason field now comes from `start-date-move-fields`, the one component that
// owns this write, and the overdue resolution dialog renders the same one.
//
// Second, edit mode was this card's own rows turning into inputs, which is
// exactly what TCSD-0247/0248/0249 replaced: on a phone a Save in the card
// body goes out of reach the moment somebody starts typing. It is now
// `CardWithModeToggle` — one Edit, one window, one Cancel, one Save, pinned on
// a phone — the same shape as every card on the Info tab. ⛔ A second
// mode-toggle component is never built.
// ============================================================================

import { useCallback, useState } from 'react';
import { CalendarCheck, CalendarClock, CheckCircle2 } from 'lucide-react';
import { useToast } from '../../shared/components';
import {
  CardWithModeToggle,
  type CardMode,
} from '../account-management/components/card-with-mode-toggle';
import { confirmAccountStarted, updateAccountStartDate } from './hooks/use-sop-data';
import { countdownLabel } from './lib/lifecycle';
import { errorMessage } from '../../shared/errors';
import { StartDateMoveFields, useStartDateMove } from './start-date-move-fields';
import { StartingServicesFields, useStartingServices } from './starting-services';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Which act the window is on.
 *
 * `choose` is the same two-way question the daily ask already put to the
 * account manager — did it open, or did it not? ⛔ The card still has ONE Save;
 * the act decides what that one Save writes, it does not add a second button.
 */
type StartAct = 'choose' | 'started' | 'moved' | 'editActual';

const DATE_INPUT_CLASS =
  'mt-1 w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-sm text-tcs-text focus:outline-none focus:ring-2 focus:ring-tcs-accent';

export function AccountStartPanel({
  facilityId,
  plannedStartDate,
  actualStartDate,
  isStarted,
  onDone,
  className,
}: {
  facilityId: string | null | undefined;
  /** SOP.start_date / facility.planned_start_date — drives the countdown. */
  plannedStartDate: string | null;
  /** facility.contract_start — the real start, drives billing. */
  actualStartDate: string | null;
  /** Already confirmed live (SOP.started_confirmed_at set or facility Active). */
  isStarted: boolean;
  onDone: () => void;
  className?: string;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<CardMode>('view');
  const [act, setAct] = useState<StartAct>('choose');
  const [actualDate, setActualDate] = useState<string>(
    actualStartDate ?? plannedStartDate ?? todayISO(),
  );

  const noFacility = !facilityId;

  const onSaved = useCallback(() => {
    setMode('view');
    setAct('choose');
    onDone();
  }, [onDone]);

  // ⛔ ONE COMPONENT OWNS THE GATED WRITE. The date box seeds the date this
  // building currently holds, because here the move usually starts from it.
  const move = useStartDateMove({
    facilityId,
    initialDate: plannedStartDate ?? todayISO(),
    onSaved,
  });
  // Which services start with it (Ben, 2026-10-23). Only asked on the first start.
  const starting = useStartingServices(isStarted ? null : facilityId);

  function handleModeChange(next: CardMode) {
    if (next === 'edit') {
      setAct(isStarted ? 'editActual' : 'choose');
      // ⛔ THE WINDOW ALWAYS OPENS EMPTY. This card stays mounted behind its own
      // window, so without this the next person gets the last move's reason
      // already typed in and Save already live (found 2026-09-22, live).
      move.reset(plannedStartDate ?? todayISO());
      setActualDate(actualStartDate ?? plannedStartDate ?? todayISO());
    }
    setMode(next);
  }

  function handleCancel() {
    setAct('choose');
    move.reset(plannedStartDate ?? todayISO());
    setActualDate(actualStartDate ?? plannedStartDate ?? todayISO());
  }

  // One Save. What it writes is whichever act the person is on, and each act is
  // ONE statement — the database function takes the whole change in one call,
  // so nothing here loops per field (PLAYBOOK §15b).
  async function handleSave(): Promise<{ ok: boolean; error: string | null }> {
    if (!facilityId) {
      return {
        ok: false,
        error: 'This card has no linked building yet, so the start date cannot be changed here.',
      };
    }
    if (act === 'moved') return move.submit();

    if (act === 'started') {
      try {
        await confirmAccountStarted(facilityId, actualDate, starting.items);
        toast.success('Account marked started');
        onSaved();
        return { ok: true, error: null };
      } catch (e) {
        return { ok: false, error: errorMessage(e, 'Something went wrong.', 'account-start-confirm') };
      }
    }

    if (act === 'editActual') {
      try {
        // ⛔ NOT GATED, AND THAT IS DELIBERATE. Correcting the real start date
        // on a building that is already live is a record being tidied, not a
        // slip, so the database asks for no reason here.
        await updateAccountStartDate(facilityId, { actual: actualDate });
        toast.success('Start date corrected');
        onSaved();
        return { ok: true, error: null };
      } catch (e) {
        return { ok: false, error: errorMessage(e, 'Something went wrong.', 'account-start-actual') };
      }
    }

    return { ok: false, error: 'Pick whether it started or whether the start date moved.' };
  }

  const hasChanges =
    act === 'moved'
      ? move.ready
      : act === 'started'
        ? !!actualDate && !starting.loading && !starting.error && starting.ready
        : act === 'editActual' && !!actualDate;

  return (
    <div className={className}>
      <CardWithModeToggle
        title="Account start"
        icon={
          isStarted ? (
            <CalendarCheck className="h-4 w-4 text-rag-green" />
          ) : (
            <CalendarClock className="h-4 w-4 text-tcs-accent-ink" />
          )
        }
        description={
          isStarted
            ? 'When this building actually opened. The real start date drives billing, the annual kicker and proration.'
            : 'When this building is due to open, and what to do when that changes.'
        }
        mode={mode}
        onModeChange={handleModeChange}
        readOnly={noFacility}
        hasChanges={hasChanges}
        onSave={handleSave}
        onCancel={handleCancel}
      >
        {(bodyMode) =>
          bodyMode === 'view' ? (
            <StartDatesReadView
              plannedStartDate={plannedStartDate}
              actualStartDate={actualStartDate}
              isStarted={isStarted}
              noFacility={noFacility}
            />
          ) : (
            <div className="space-y-3">
              {act === 'choose' && (
                <StartActChoice
                  onStarted={() => {
                    setActualDate(plannedStartDate ?? todayISO());
                    setAct('started');
                  }}
                  onMoved={() => setAct('moved')}
                />
              )}

              {act === 'moved' && (
                <>
                  <StartActBack label="It started instead" onBack={() => setAct('choose')} />
                  <StartDateMoveFields move={move} />
                </>
              )}

              {act === 'started' && (
                <>
                  <StartActBack label="The start date moved instead" onBack={() => setAct('choose')} />
                  <ActualDateField
                    value={actualDate}
                    onChange={setActualDate}
                    label="The day it actually opened"
                    note="This marks the building live and tells Finance to start billing and start paying. A day that has not happened yet is refused — if that is the day it is due to open, say the start date moved instead."
                  />
                  <StartingServicesFields state={starting} />
                </>
              )}

              {act === 'editActual' && (
                <ActualDateField
                  value={actualDate}
                  onChange={setActualDate}
                  label="Actual start date"
                  note="Drives billing, the annual kicker and proration. Correcting it on a building that is already open is a record being tidied, so no reason is asked for."
                />
              )}
            </div>
          )
        }
      </CardWithModeToggle>
    </div>
  );
}

// ============================================================================
// ⛔ EVERY PIECE BELOW IS DECLARED AT MODULE SCOPE. Declared inside the panel
// they would be a fresh component type on every render, React would remount
// them on each keystroke, and every text box here would lose focus after one
// character — live, with every gate green over it (TCSD-0232). There is no
// lint in this repo that catches it.
// ============================================================================

function StartDatesReadView({
  plannedStartDate,
  actualStartDate,
  isStarted,
  noFacility,
}: {
  plannedStartDate: string | null;
  actualStartDate: string | null;
  isStarted: boolean;
  noFacility: boolean;
}) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-tcs-text-muted">Planned start</div>
          <div className="font-medium text-tcs-text">{plannedStartDate ?? '—'}</div>
        </div>
        <div>
          <div className="text-tcs-text-muted">Actual start (billing)</div>
          <div className="font-medium text-tcs-text">{actualStartDate ?? '—'}</div>
        </div>
      </div>
      {!isStarted && (
        <div className="mt-2 text-xs font-medium text-tcs-text-secondary">
          {countdownLabel({ start_date: plannedStartDate }) ?? 'No start date set'}
        </div>
      )}
      {isStarted && (
        <div className="mt-2 inline-flex rounded-full bg-rag-green/10 px-2 py-0.5 text-[10px] font-semibold text-rag-green">
          Started
        </div>
      )}
      {noFacility && (
        <p className="mt-2 text-xs text-tcs-text-muted">
          No linked building yet — the start can be confirmed once this card has one.
        </p>
      )}
    </div>
  );
}

function StartActChoice({ onStarted, onMoved }: { onStarted: () => void; onMoved: () => void }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-tcs-text-secondary">Which is it?</p>
      <button
        type="button"
        data-testid="start-act-started"
        onClick={onStarted}
        className="flex w-full items-center gap-3 rounded-lg border border-tcs-border p-3 text-left transition-colors hover:bg-tcs-bg-muted"
      >
        <CheckCircle2 className="h-5 w-5 shrink-0 text-rag-green" />
        <span>
          <span className="block text-sm font-medium text-tcs-text">It started</span>
          <span className="block text-xs text-tcs-text-secondary">Set the real start date and mark it live.</span>
        </span>
      </button>
      <button
        type="button"
        data-testid="start-act-moved"
        onClick={onMoved}
        className="flex w-full items-center gap-3 rounded-lg border border-tcs-border p-3 text-left transition-colors hover:bg-tcs-bg-muted"
      >
        <CalendarClock className="h-5 w-5 shrink-0 text-tcs-accent-ink" />
        <span>
          <span className="block text-sm font-medium text-tcs-text">The start date moved</span>
          <span className="block text-xs text-tcs-text-secondary">Pick a new planned start date, or say it is not known yet.</span>
        </span>
      </button>
    </div>
  );
}

function StartActBack({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="text-xs text-tcs-text-muted underline underline-offset-2 hover:text-tcs-text-secondary"
    >
      {label}
    </button>
  );
}

function ActualDateField({
  value,
  onChange,
  label,
  note,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  note: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-tcs-text-secondary">{label}</span>
      <input
        type="date"
        data-testid="start-date-actual"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={DATE_INPUT_CLASS}
      />
      <span className="mt-1 block text-[11px] text-tcs-text-secondary">{note}</span>
    </label>
  );
}
